import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Badge, Button, Card, Col, DatePicker, Empty, Input, Result, Row, Select, Space, Spin, Tabs, Tag, Tooltip,
  Typography,
} from 'antd';
import {
  BarChartOutlined, ClearOutlined, InfoCircleOutlined, ReloadOutlined, WarningOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTheme } from '../../context/ThemeContext';
import BiHarita from './BiHarita';
import BiKaynaklar from './BiKaynaklar';
import BiKontrol from './BiKontrol';
import BiOzet from './BiOzet';
import BiSatirlar from './BiSatirlar';
import {
  ANA_SUZGECLER, BOS, EK_RENK, NEGATIF_RENK, ORTAK_SUZGECLER, RAPOR_YUVALARI, UZUN_ZAMAN_ASIMI, degerMetni, hata, para,
  sayi, useBiApi, yerelOku, yerelYaz, zamanDamgasi,
} from './biOrtak';
import '../ortak/nfmodules.css';
import './BiDemoPage.css';

/**
 * BI v2 teyit ekranı (27.10.2026) — dev.atmturkiye.com/bi-demo.
 * Power BI'a verilen biv2-* raporlarının (masraflar, giderler, gelecek giderler, gelirler, gelecek gelirler)
 * rakamlarını il / ilçe / proje / banka / grup kırılımlarında, haritada ve satır satır gösterir.
 * Toplama sunucuda (routes/bi_v2.py, önbellekli); tarayıcıya ham rapor inmez. Erişim: Genel / Finans (okuma).
 */
const { Title, Text, Paragraph } = Typography;
const SECILI_ANAHTAR = 'biDemoRapor';
const SEKME_ANAHTAR = 'biDemoSekme';
const SEKMELER = ['ozet', 'harita', 'satirlar', 'kontrol', 'kaynaklar'];

function KpiKart({ yuva, veri, yukleniyor, secili, onSec }) {
  const rapor = yuva.rapor;
  let govde;
  if (!rapor) {
    govde = (
      <>
        <Text type="secondary">Henüz tanımlı değil</Text>
        <div><Tag style={{ marginTop: 6 }}>{yuva.key}</Tag></div>
      </>
    );
  } else if (!veri && yukleniyor) {
    govde = <Space><Spin size="small" /><Text type="secondary">Üretiliyor…</Text></Space>;
  } else if (veri?.hata) {
    govde = (
      <>
        <Text type="danger" strong>Üretilemedi</Text>
        <Paragraph type="danger" style={{ fontSize: 12, marginBottom: 0 }} ellipsis={{ rows: 3, tooltip: veri.hata }}>{veri.hata}</Paragraph>
      </>
    );
  } else if (veri) {
    govde = (
      <>
        <div className="bi-kpi-tutar" style={veri.tutar < 0 ? { color: NEGATIF_RENK } : undefined}>{para(veri.tutar)}</div>
        <Text type="secondary" style={{ fontSize: 12 }}>{sayi(veri.satir)} satır · {sayi(veri.lokasyon)} lokasyon</Text>
        <div style={{ marginTop: 4 }}>
          {veri.uyari
            ? <Tag color="warning" icon={<WarningOutlined />}>{veri.uyari} kontrol uyarısı</Tag>
            : <Tag color="success">Kontroller tamam</Tag>}
          {veri.sure !== null && veri.sure !== undefined && (
            <Tooltip title={`Veri zamanı: ${zamanDamgasi(veri.ts)}`}><Text type="secondary" style={{ fontSize: 11 }}>{Number(veri.sure).toLocaleString('tr-TR')} sn</Text></Tooltip>
          )}
        </div>
      </>
    );
  } else {
    govde = <Text type="secondary">—</Text>;
  }
  return (
    <Card size="small" hoverable={!!rapor} onClick={rapor ? () => onSec(rapor.key) : undefined}
      className={`bi-kpi-kart${secili ? ' secili' : ''}${rapor ? '' : ' pasif'}`} style={{ '--bi-renk': yuva.renk }}>
      <div className="bi-kpi-baslik">
        <Space size={6}>
          <span className="bi-nokta" style={{ background: yuva.renk }} />
          <Text strong>{yuva.baslik}</Text>
          {yuva.gelecek && (
            <Tooltip title="Gelecek raporları gelecek aydan başlayan 12 ayı kapsar (bu ay Giderler / Gelirler'de); Yıl / dönem süzgeci bunlara da uygulanır. 12 ayın tamamı için Yıl: Tümü seçin.">
              <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
            </Tooltip>
          )}
        </Space>
        {rapor && yukleniyor && veri && <Spin size="small" />}
      </div>
      {govde}
    </Card>
  );
}

export default function BiDemoPage() {
  const api = useBiApi();
  const tema = useTheme();
  const darkMode = !!tema?.darkMode;
  const buYil = dayjs().year();

  const [raporlar, setRaporlar] = useState(null);
  const [raporHata, setRaporHata] = useState(null); // {durum, metin}
  const [yil, setYil] = useState(buYil); // 0 = tümü
  const [bas, setBas] = useState(null);
  const [bit, setBit] = useState(null);
  const [kpi, setKpi] = useState({});
  const [kpiYukleniyor, setKpiYukleniyor] = useState(false);
  const [kpiHata, setKpiHata] = useState(null);
  const [yenileniyor, setYenileniyor] = useState(false);
  const [surum, setSurum] = useState(0);
  const [seciliKey, setSeciliKey] = useState(() => yerelOku(SECILI_ANAHTAR, null));
  const [sekme, setSekme] = useState(() => {
    const k = yerelOku(SEKME_ANAHTAR, 'ozet');
    return SEKMELER.includes(k) ? k : 'ozet';
  });
  const [filtre, setFiltre] = useState({});
  const [ara, setAra] = useState('');
  const [araGirdi, setAraGirdi] = useState('');
  const [ekKolonlar, setEkKolonlar] = useState([]);
  const [boyut1, setBoyut1] = useState('İl');
  const [boyut2, setBoyut2] = useState(null);
  const [olcu, setOlcu] = useState('Tutar');
  const [secenek, setSecenek] = useState({ key: null, veri: null, yukleniyor: false, hata: null });
  const kpiNo = useRef(0);
  const secenekNo = useRef(0);
  const yenileRef = useRef(false); // Yenile sırasında rapor sayısı değişirse ikinci (önbellekli) /kpi atılmasın

  // --- Rapor listesi ------------------------------------------------------
  const raporlariYukle = useCallback(() => {
    setRaporHata(null);
    return api.get('/raporlar')
      .then((d) => setRaporlar(d?.raporlar || []))
      .catch((e) => setRaporHata({ durum: e?.response?.status, metin: hata(e) }));
  }, [api]);
  useEffect(() => { raporlariYukle(); }, [raporlariYukle]);

  const yuvalar = useMemo(() => {
    const liste = raporlar || [];
    const kullanilan = new Set();
    const out = RAPOR_YUVALARI.map((y) => {
      const r = liste.find((x) => x.kalem_turu === y.kalem && !kullanilan.has(x.key))
        || liste.find((x) => x.key === y.key && !kullanilan.has(x.key));
      if (r) kullanilan.add(r.key);
      return { ...y, rapor: r || null };
    });
    liste.filter((r) => !kullanilan.has(r.key)).forEach((r) => out.push({
      kalem: r.kalem_turu, baslik: r.ad.replace(/^BIv2\s*-\s*/i, ''), key: r.key, renk: EK_RENK, rapor: r,
    }));
    return out;
  }, [raporlar]);
  const tanimli = yuvalar.filter((y) => y.rapor);
  const aktifYuva = tanimli.find((y) => y.rapor.key === seciliKey) || tanimli[0] || null;
  const rapor = aktifYuva?.rapor || null;

  // --- Global dönem + KPI -------------------------------------------------
  const donem = useMemo(() => ({ yil: yil || undefined, bas: bas || undefined, bit: bit || undefined }), [yil, bas, bit]);
  const kpiYukle = useCallback((yenile = false) => {
    kpiNo.current += 1;
    const no = kpiNo.current;
    setKpiYukleniyor(true);
    setKpiHata(null);
    return api.get('/kpi', { ...donem, yenile: yenile || undefined }, UZUN_ZAMAN_ASIMI)
      .then((d) => {
        if (no !== kpiNo.current) return;
        const m = {};
        (d?.raporlar || []).forEach((r) => { m[r.key] = r; });
        setKpi(m);
      })
      .catch((e) => { if (no === kpiNo.current) setKpiHata(hata(e)); })
      .finally(() => { if (no === kpiNo.current) setKpiYukleniyor(false); });
  }, [api, donem]);
  const raporSayisi = raporlar?.length || 0;
  useEffect(() => { if (raporSayisi && !yenileRef.current) kpiYukle(); }, [kpiYukle, raporSayisi]);

  // --- Seçili raporun süzgeç seçenekleri -----------------------------------
  const raporKey = rapor?.key || null;
  const secenekYukle = useCallback(() => {
    if (!raporKey) return;
    secenekNo.current += 1;
    const no = secenekNo.current;
    setSecenek((s) => ({ ...s, key: raporKey, yukleniyor: true, hata: null }));
    api.rapor(raporKey, 'secenekler')
      .then((d) => { if (no === secenekNo.current) setSecenek({ key: raporKey, veri: d?.secenekler || {}, yukleniyor: false, hata: null }); })
      .catch((e) => { if (no === secenekNo.current) setSecenek({ key: raporKey, veri: null, yukleniyor: false, hata: hata(e) }); });
  }, [api, raporKey]);
  useEffect(() => { secenekYukle(); }, [secenekYukle, surum]);
  const secenekler = secenek.key === raporKey ? secenek.veri : null;

  const yenile = () => {
    setYenileniyor(true);
    yenileRef.current = true;
    raporlariYukle()
      .then(() => kpiYukle(true))
      .then(() => setSurum((s) => s + 1))
      .finally(() => {
        yenileRef.current = false;
        setYenileniyor(false);
      });
  };

  // --- Rapor / sekme seçimi -----------------------------------------------
  const raporSec = (key) => {
    if (key === raporKey) return;
    const yeni = tanimli.find((y) => y.rapor.key === key)?.rapor;
    setSeciliKey(key);
    yerelYaz(SECILI_ANAHTAR, key);
    // Lokasyon boyutu süzgeçleri korunur; rapora özgü olanlar (Grup, Tip, Dağıtım, Kaynak...) bırakılır.
    setFiltre((f) => Object.fromEntries(Object.entries(f).filter(([k]) => ORTAK_SUZGECLER.has(k))));
    setEkKolonlar((e) => e.filter((k) => ORTAK_SUZGECLER.has(k)));
    const boyutlar = new Set((yeni?.kolonlar || []).filter((k) => k.boyut).map((k) => k.ad));
    if (!boyutlar.has(boyut1)) setBoyut1('İl');
    if (boyut2 && !boyutlar.has(boyut2)) setBoyut2(null);
    if (!(yeni?.kolonlar || []).some((k) => k.olcu && k.ad === olcu)) setOlcu('Tutar');
  };
  const sekmeSec = (k) => {
    setSekme(k);
    yerelYaz(SEKME_ANAHTAR, k);
  };

  // --- Süzgeçler ----------------------------------------------------------
  const temizFiltre = useMemo(() => {
    const o = {};
    Object.entries(filtre).forEach(([k, v]) => { if (Array.isArray(v) && v.length) o[k] = v; });
    return o;
  }, [filtre]);
  const params = useMemo(() => ({
    ...donem,
    f: Object.keys(temizFiltre).length ? JSON.stringify(temizFiltre) : undefined,
    ara: ara || undefined,
  }), [donem, temizFiltre, ara]);

  const kolonFiltre = (k, v) => setFiltre((f) => ({ ...f, [k]: v }));
  const degerKaldir = (k, v) => setFiltre((f) => ({ ...f, [k]: (f[k] || []).filter((x) => x !== v) }));
  const hepsiniTemizle = () => {
    setFiltre({});
    setAra('');
    setAraGirdi('');
  };
  const drill = (ciftler) => {
    setFiltre((f) => {
      const y = { ...f };
      ciftler.forEach(([k, v]) => { y[k] = [v === null || v === undefined || v === '' ? BOS : v]; });
      return y;
    });
    sekmeSec('satirlar');
  };

  const boyutKolonlari = useMemo(() => (rapor?.kolonlar || []).filter((k) => k.boyut).map((k) => k.ad), [rapor]);
  const olcuSecenek = useMemo(() => (rapor?.kolonlar || []).filter((k) => k.olcu)
    .map((k) => ({ value: k.ad, label: k.ad })), [rapor]);
  const gorunenSuzgecler = useMemo(() => {
    const s = [];
    [...ANA_SUZGECLER.filter((k) => boyutKolonlari.includes(k)), ...ekKolonlar, ...Object.keys(filtre)]
      .forEach((k) => { if (!s.includes(k)) s.push(k); });
    return s;
  }, [boyutKolonlari, ekKolonlar, filtre]);
  const eklenebilir = boyutKolonlari.filter((k) => !gorunenSuzgecler.includes(k)).map((k) => ({ value: k, label: k }));
  const secenekListesi = (k) => (secenekler?.[k] || []).map((o) => ({
    value: o.deger === null || o.deger === undefined ? BOS : o.deger,
    label: `${degerMetni(o.deger)} (${sayi(o.adet)})`,
  }));
  const etiketler = Object.entries(temizFiltre).flatMap(([k, vs]) => vs.map((v) => (
    <Tag key={`${k}|${v}`} closable color="blue" onClose={(e) => { e.preventDefault(); degerKaldir(k, v); }}>
      {k}: {degerMetni(v)}
    </Tag>
  )));
  if (ara) {
    etiketler.push(
      <Tag key="__ara" closable color="purple" onClose={(e) => { e.preventDefault(); setAra(''); setAraGirdi(''); }}>
        Ara: {ara}
      </Tag>,
    );
  }

  // --- Türetilmiş kart: Gelir − (Masraf + Gider) ---------------------------
  const kalemTutari = (kalem) => {
    const y = yuvalar.find((x) => x.kalem === kalem && x.rapor);
    const v = y && kpi[y.rapor.key];
    return v && !v.hata && typeof v.tutar === 'number' ? v.tutar : null;
  };
  const gelir = kalemTutari('Gelir');
  const masraf = kalemTutari('Masraf');
  const gider = kalemTutari('Gider');
  const eksik = [['Gelir', gelir], ['Masraf', masraf], ['Gider', gider]].filter(([, v]) => v === null).map(([a]) => a);
  const net = eksik.length ? null : gelir - (masraf + gider);
  const donemMetni = [yil ? `${yil}` : 'Tüm yıllar', bas ? `${bas} →` : null, bit ? `→ ${bit}` : null].filter(Boolean).join(' ');

  // --- Erken dönüşler -----------------------------------------------------
  if (raporHata?.durum === 403) return <Result status="403" title="Erişim yok" subTitle={raporHata.metin} />;
  if (raporHata) {
    return (
      <Result status="error" title="BI v2 raporları alınamadı" subTitle={raporHata.metin}
        extra={<Button type="primary" icon={<ReloadOutlined />} onClick={raporlariYukle}>Yeniden dene</Button>} />
    );
  }
  if (!raporlar) return <div style={{ padding: 48, textAlign: 'center' }}><Spin tip="Raporlar yükleniyor…"><div style={{ padding: 30 }} /></Spin></div>;

  const yilSecenek = [{ value: 0, label: 'Tümü' }];
  for (let y = buYil + 2; y >= 2015; y -= 1) yilSecenek.push({ value: y, label: String(y) });

  const sekmeler = rapor ? [
    { key: 'ozet', label: 'Özet', children: (
      <BiOzet api={api} rapor={rapor} params={params} boyut1={boyut1} boyut2={boyut2} setBoyut1={setBoyut1}
        setBoyut2={setBoyut2} olcu={olcu} aktif={sekme === 'ozet'} surum={surum} renk={aktifYuva.renk} onDrill={drill} />) },
    { key: 'harita', label: 'Harita', children: (
      <BiHarita api={api} rapor={rapor} params={params} olcu={olcu} aktif={sekme === 'harita'} surum={surum}
        renk={aktifYuva.renk} darkMode={darkMode} />) },
    { key: 'satirlar', label: 'Satırlar', children: (
      <BiSatirlar api={api} rapor={rapor} params={params} aktif={sekme === 'satirlar'} surum={surum} />) },
    { key: 'kontrol', label: (
      <span>Kontrol {kpi[rapor.key]?.uyari ? <Badge count={kpi[rapor.key].uyari} size="small" color="orange" /> : null}</span>),
    children: <BiKontrol api={api} rapor={rapor} aktif={sekme === 'kontrol'} surum={surum} /> },
    { key: 'kaynaklar', label: 'Kaynaklar', children: (
      <BiKaynaklar api={api} aktif={sekme === 'kaynaklar'} seciliKey={rapor.key} />) },
  ] : [];

  return (
    <div className="nf-module bi-demo">
      <Title level={3} style={{ marginTop: 0 }}>
        <BarChartOutlined style={{ marginRight: 8 }} />BI v2 — Rakam Teyit Ekranı
      </Title>

      {/* Global dönem süzgeci */}
      <Space wrap align="end" style={{ marginBottom: 12 }}>
        <div className="bi-suzgec">
          <span className="bi-suzgec-ad">Yıl</span>
          <Select value={yil} onChange={setYil} options={yilSecenek} style={{ width: 110 }} />
        </div>
        <div className="bi-suzgec">
          <span className="bi-suzgec-ad">Dönem başlangıç</span>
          <DatePicker picker="month" format="MM.YYYY" placeholder="Başlangıç" value={bas ? dayjs(`${bas}-01`) : null}
            onChange={(v) => setBas(v ? v.format('YYYY-MM') : null)} style={{ width: 130 }}
            disabledDate={(d) => !!bit && d.format('YYYY-MM') > bit} />
        </div>
        <div className="bi-suzgec">
          <span className="bi-suzgec-ad">Dönem bitiş</span>
          <DatePicker picker="month" format="MM.YYYY" placeholder="Bitiş" value={bit ? dayjs(`${bit}-01`) : null}
            onChange={(v) => setBit(v ? v.format('YYYY-MM') : null)} style={{ width: 130 }}
            disabledDate={(d) => !!bas && d.format('YYYY-MM') < bas} />
        </div>
        <Tooltip title="Önbelleği atlar: beş rapor kaynak tablolardan (canlı veritabanı) yeniden üretilir; 1-2 dakika sürebilir. Normalde veriler 15 dakikalık önbellekten gelir.">
          <Button icon={<ReloadOutlined />} onClick={yenile} loading={yenileniyor}>Yenile</Button>
        </Tooltip>
        {kpiYukleniyor && !yenileniyor && <Text type="secondary"><Spin size="small" /> Raporlar hesaplanıyor (ilk açılış 10-60 sn)…</Text>}
      </Space>

      {!raporlar.length && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="Henüz kayıtlı biv2 raporu yok"
          description="Rapor modülleri (services/bi_v2/raporlar) yapım aşamasında; tanımlandıkça kartlar dolar. Yenile ile tekrar bakılabilir." />
      )}
      {kpiHata && <Alert type="error" showIcon style={{ marginBottom: 12 }} message="KPI'lar alınamadı" description={kpiHata}
        action={<Button size="small" onClick={() => kpiYukle()}>Tekrar dene</Button>} />}

      {/* KPI kartları */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {yuvalar.map((y) => (
          <Col key={y.key} xs={24} sm={12} lg={8} xxl={4}>
            <KpiKart yuva={y} veri={y.rapor ? kpi[y.rapor.key] : null} yukleniyor={kpiYukleniyor}
              secili={!!rapor && y.rapor?.key === rapor.key} onSec={raporSec} />
          </Col>
        ))}
        <Col xs={24} sm={12} lg={8} xxl={4}>
          <Card size="small" className="bi-kpi-kart" style={{ '--bi-renk': '#8c8c8c' }}>
            <div className="bi-kpi-baslik">
              <Space size={6}>
                <span className="bi-nokta" style={{ background: '#8c8c8c' }} />
                <Text strong>Gelir − (Masraf + Gider)</Text>
                <Tooltip title="Gösterge niteliğindedir: raporların tarih esasları farklıdır (gelir fatura tarihi, gider ödeme vadesi, masraf belge / maliyet tarihi). Gelecek dönem raporları dahil değildir.">
                  <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                </Tooltip>
              </Space>
            </div>
            {net === null ? (
              <Text type="secondary">{kpiYukleniyor ? 'Hesaplanıyor…' : `Eksik: ${eksik.join(', ')}`}</Text>
            ) : (
              <div className="bi-kpi-tutar" style={{ color: net < 0 ? '#cf1322' : '#389e0d' }}>{para(net)}</div>
            )}
            <Text type="secondary" style={{ fontSize: 12 }}>{donemMetni} · gösterge niteliğinde</Text>
          </Card>
        </Col>
      </Row>

      {/* Seçili rapor */}
      {rapor ? (
        <Card size="small" className="bi-panel" style={{ '--bi-renk': aktifYuva.renk }}
          title={(
            <Space wrap size={8}>
              <span className="bi-nokta" style={{ background: aktifYuva.renk }} />
              <span>{rapor.ad}</span>
              <Tag>{rapor.key}</Tag>
              <Tag color="default">{rapor.kalem_turu}</Tag>
            </Space>
          )}>
          {rapor.aciklama && (
            <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 10 }} ellipsis={{ rows: 2, expandable: true, symbol: 'devamı' }}>
              {rapor.aciklama}
            </Paragraph>
          )}

          {/* Süzgeç çubuğu: Özet, Harita ve Satırlar sekmelerine uygulanır */}
          <div className="bi-suzgec-cubugu">
            <Space wrap align="end" size={[8, 8]}>
              <div className="bi-suzgec">
                <span className="bi-suzgec-ad">Lokasyon ara (no / ad)</span>
                <Input.Search allowClear placeholder="Lokasyon no ya da adı" value={araGirdi} style={{ width: 220 }}
                  onChange={(e) => setAraGirdi(e.target.value)} onSearch={(v) => setAra((v || '').trim())} />
              </div>
              {gorunenSuzgecler.map((k) => {
                const ops = secenekListesi(k);
                return (
                  <div className="bi-suzgec" key={k}>
                    <span className="bi-suzgec-ad">{k}</span>
                    <Select mode={ops.length ? 'multiple' : 'tags'} allowClear showSearch optionFilterProp="label"
                      maxTagCount="responsive" placeholder="Tümü" value={filtre[k] || []} onChange={(v) => kolonFiltre(k, v)}
                      options={ops} loading={secenek.yukleniyor} style={{ width: 190 }}
                      notFoundContent={secenek.yukleniyor ? <Spin size="small" /> : 'Seçenek yok (değer yazılabilir)'} />
                  </div>
                );
              })}
              {eklenebilir.length > 0 && (
                <div className="bi-suzgec">
                  <span className="bi-suzgec-ad">Süzgeç ekle</span>
                  <Select showSearch optionFilterProp="label" placeholder="+ kolon" value={null} options={eklenebilir}
                    onChange={(k) => k && setEkKolonlar((e) => (e.includes(k) ? e : [...e, k]))} style={{ width: 170 }} />
                </div>
              )}
              <div className="bi-suzgec">
                <span className="bi-suzgec-ad">Ölçü</span>
                <Select value={olcu} onChange={setOlcu} style={{ width: 190 }}
                  options={olcuSecenek.length ? olcuSecenek : [{ value: 'Tutar', label: 'Tutar' }]} />
              </div>
            </Space>
            {secenek.hata && <Alert type="warning" showIcon style={{ marginTop: 8 }} message="Süzgeç seçenekleri alınamadı" description={secenek.hata} />}
            <div className="bi-etiketler">
              {etiketler.length ? (
                <Space wrap size={[4, 4]}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Etkin süzgeçler:</Text>
                  {etiketler}
                  <Button size="small" type="link" icon={<ClearOutlined />} onClick={hepsiniTemizle}>Tümünü temizle</Button>
                </Space>
              ) : (
                <Text type="secondary" style={{ fontSize: 12 }}>Süzgeç yok — {donemMetni} için tüm satırlar.</Text>
              )}
            </div>
          </div>

          <Tabs activeKey={sekme} onChange={sekmeSec} items={sekmeler} />
        </Card>
      ) : (
        <Card size="small"><Empty description="Seçilebilecek rapor yok" /></Card>
      )}
    </div>
  );
}
